import React, { useState, useCallback } from 'react';
import {
  Box, Typography, Card, CardContent, Chip, Button, TextField,
  CircularProgress, Alert, Stack, Divider, IconButton, Tooltip,
  Avatar, InputAdornment, ThemeProvider, createTheme, CssBaseline,
  useMediaQuery,
} from '@mui/material';
import {
  MedicalServices, PictureAsPdf, OpenInNew, Logout, Search,
  Refresh, HourglassEmpty, CheckCircle, Schedule, ErrorOutline,
  Visibility, VisibilityOff, CalendarMonth, Badge,
} from '@mui/icons-material';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || '';

const theme = createTheme({
  palette: {
    mode: 'light',
    primary: { main: '#0ea5e9' },
    secondary: { main: '#059669' },
    background: { default: '#f0f9ff', paper: '#ffffff' },
  },
  shape: { borderRadius: 12 },
  typography: { fontFamily: '"Satoshi", -apple-system, BlinkMacSystemFont, "Segoe UI", "Roboto", sans-serif' },
});

const MODALITY_COLORS = {
  CT: '#6366f1', MR: '#8b5cf6', XR: '#0ea5e9', US: '#059669',
  CR: '#0284c7', DR: '#0369a1', MG: '#db2777', PT: '#d97706',
  NM: '#dc2626', OT: '#64748b',
};

const STATUS_CONFIG = {
  completed: { label: 'Report Ready', color: 'success', icon: <CheckCircle sx={{ fontSize: 14 }} /> },
  in_progress: { label: 'Being Reported', color: 'warning', icon: <HourglassEmpty sx={{ fontSize: 14 }} /> },
  pending: { label: 'Pending', color: 'info', icon: <Schedule sx={{ fontSize: 14 }} /> },
  not_available: { label: 'No Report Yet', color: 'default', icon: <ErrorOutline sx={{ fontSize: 14 }} /> },
};

function StatusChip({ status }) {
  const cfg = STATUS_CONFIG[status] || STATUS_CONFIG.not_available;
  return (
    <Chip icon={cfg.icon} label={cfg.label} color={cfg.color} size="small" variant="outlined" />
  );
}

function formatDate(d) {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-GB', { day: '2-digit', month: 'long', year: 'numeric' });
}

// ── Login screen ─────────────────────────────────────────────────────────────

function LoginScreen({ onLogin }) {
  const [challengeToken, setChallengeToken] = useState(null);
  const [code, setCode] = useState('');
  const [mrn, setMrn] = useState('');
  const [dob, setDob] = useState('');
  const [showDob, setShowDob] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (challengeToken) {
        const res = await axios.post(`${API_URL}/api/patient-portal/verify`, { challengeToken, code });
        onLogin(res.data.token, res.data.patient);
      } else {
        const res = await axios.post(`${API_URL}/api/patient-portal/auth`, { mrn, dob });
        setChallengeToken(res.data.challengeToken);
      }
    } catch (err) {
      setError(err.response?.data?.error || 'Login failed. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        background: 'linear-gradient(160deg, #e0f2fe 0%, #f0fdf4 60%, #fafafa 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Box sx={{ width: '100%', maxWidth: 420 }}>
        {/* Logo / Brand */}
        <Stack direction="row" alignItems="center" spacing={1.5} justifyContent="center" mb={4}>
          <Box
            sx={{
              width: 44, height: 44, borderRadius: 2,
              background: 'linear-gradient(135deg, #0ea5e9, #059669)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
            }}
          >
            <MedicalServices sx={{ color: '#fff', fontSize: 24 }} />
          </Box>
          <Box>
            <Typography variant="h6" fontWeight={800} color="primary.dark" lineHeight={1.1}>
              DycoVue
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Patient Results Portal
            </Typography>
          </Box>
        </Stack>

        <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 3 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h5" fontWeight={700} mb={0.5}>
              Access Your Results
            </Typography>
            <Typography variant="body2" color="text.secondary" mb={3}>
              Enter your patient ID and date of birth to view your imaging studies and reports.
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }} onClose={() => setError('')}>
                {error}
              </Alert>
            )}

            <Box component="form" onSubmit={handleSubmit}>
              <TextField
                fullWidth
                label="Patient MRN / Hospital Number"
                value={mrn}
                onChange={(e) => setMrn(e.target.value)}
                required
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <Badge fontSize="small" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                }}
                sx={{ mb: 2 }}
              />
              <TextField
                fullWidth
                label="Date of Birth"
                value={dob}
                onChange={(e) => setDob(e.target.value)}
                required
                placeholder="DD/MM/YYYY or YYYYMMDD"
                type={showDob ? 'text' : 'password'}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CalendarMonth fontSize="small" sx={{ color: 'text.disabled' }} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        size="small"
                        onClick={() => setShowDob(v => !v)}
                        edge="end"
                      >
                        {showDob ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
                helperText="Enter as DD/MM/YYYY (e.g. 15/01/1990) or 8-digit YYYYMMDD"
                sx={{ mb: 3 }}
              />
              {challengeToken && <><Alert severity="info">If your details match an enrolled contact, a code has been emailed. Contact your facility if it does not arrive.</Alert><TextField label="One-time code" value={code} onChange={event => setCode(event.target.value)} autoComplete="one-time-code" inputProps={{ maxLength: 6, inputMode: "numeric" }} /></>}
              <Button
                type="submit"
                fullWidth
                variant="contained"
                size="large"
                disabled={loading || !mrn.trim() || !dob.trim()}
                sx={{
                  py: 1.5, fontWeight: 700, borderRadius: 2,
                  background: 'linear-gradient(135deg, #0ea5e9, #059669)',
                }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : challengeToken ? 'Verify access' : 'Send access code'}
              </Button>
            </Box>
          </CardContent>
        </Card>

        <Typography variant="caption" color="text.secondary" display="block" textAlign="center" mt={3}>
          For account help, contact your radiology department. Access requires a code sent to your enrolled email contact.
        </Typography>
      </Box>
    </Box>
  );
}

// ── Study card ────────────────────────────────────────────────────────────────

function StudyCard({ study }) {
  const modalityColor = MODALITY_COLORS[study.modality] || '#64748b';

  return (
    <Card
      elevation={0}
      sx={{
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2.5,
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: 4 },
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Stack direction="row" alignItems="flex-start" spacing={2}>
          {/* Modality badge */}
          <Box
            sx={{
              minWidth: 52, height: 52, borderRadius: 1.5,
              background: `linear-gradient(135deg, ${modalityColor}, ${modalityColor}bb)`,
              display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}
          >
            <Typography sx={{ color: '#fff', fontWeight: 800, fontSize: 13, letterSpacing: 0.5 }}>
              {study.modality || '?'}
            </Typography>
          </Box>

          {/* Main info */}
          <Box flex={1} minWidth={0}>
            <Stack direction="row" alignItems="center" spacing={1} flexWrap="wrap" mb={0.5}>
              <Typography variant="body1" fontWeight={700} noWrap>
                {study.studyDescription || study.modality || 'Imaging Study'}
              </Typography>
              <StatusChip status={study.reportStatus} />
            </Stack>
            <Typography variant="caption" color="text.secondary">
              {formatDate(study.studyDate)}
              {study.facilityName ? ` · ${study.facilityName}` : ''}
            </Typography>
          </Box>

          {/* Action buttons */}
          <Stack direction="row" spacing={0.5} flexShrink={0}>
            {study.reportStatus === 'completed' && study.reportId && (
              <Tooltip title="View Report PDF">
                <IconButton
                  size="small"
                  color="success"
                  onClick={() => window.open(`/secure-pdf/${study.reportId}#access_token=${encodeURIComponent(sessionStorage.getItem('patient_portal_token') || '')}`, '_blank', 'noopener,noreferrer')}
                >
                  <PictureAsPdf />
                </IconButton>
              </Tooltip>
            )}
            {study.hasImages && (
              <Tooltip title="View Images">
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => window.open(`/secure-viewer/${study._id}#access_token=${encodeURIComponent(sessionStorage.getItem('patient_portal_token') || '')}`, '_blank', 'noopener,noreferrer')}
                >
                  <OpenInNew />
                </IconButton>
              </Tooltip>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
}

// ── Main portal ───────────────────────────────────────────────────────────────

export default function PatientPortal() {
  const isMobile = useMediaQuery('(max-width:600px)');

  // Auth state — stored in sessionStorage so it clears on tab close
  const [token, setToken] = useState(() => sessionStorage.getItem('patient_portal_token'));
  const [patient, setPatient] = useState(() => {
    try { return JSON.parse(sessionStorage.getItem('patient_portal_info') || 'null'); } catch { return null; }
  });

  // Study list state
  const [studies, setStudies] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loaded, setLoaded] = useState(false);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');

  const handleLogin = (tok, pat) => {
    sessionStorage.setItem('patient_portal_token', tok);
    sessionStorage.setItem('patient_portal_info', JSON.stringify(pat));
    setToken(tok);
    setPatient(pat);
  };

  const handleLogout = () => {
    sessionStorage.removeItem('patient_portal_token');
    sessionStorage.removeItem('patient_portal_info');
    setToken(null);
    setPatient(null);
    setStudies([]);
    setLoaded(false);
  };

  const fetchStudies = useCallback(async (tok = token) => {
    if (!tok) return;
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/api/patient-portal/studies`, {
        headers: { Authorization: `Bearer ${tok}` },
      });
      setStudies(res.data.studies || []);
      setLoaded(true);
    } catch (err) {
      if (err.response?.status === 401) {
        // Token expired
        handleLogout();
      } else {
        setError(err.response?.data?.error || 'Failed to load studies.');
      }
    } finally {
      setLoading(false);
    }
  }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

  // Auto-fetch on first render when already logged in
  React.useEffect(() => {
    if (token && !loaded) fetchStudies();
  }, [token, loaded, fetchStudies]);

  // Trigger fetch after login
  const handleLoginSuccess = (tok, pat) => {
    handleLogin(tok, pat);
    fetchStudies(tok);
  };

  if (!token) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <LoginScreen onLogin={handleLoginSuccess} />
      </ThemeProvider>
    );
  }

  const filtered = studies.filter(s => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (
      (s.studyDescription || '').toLowerCase().includes(q) ||
      (s.modality || '').toLowerCase().includes(q) ||
      (s.facilityName || '').toLowerCase().includes(q)
    );
  });

  const readyCount = studies.filter(s => s.reportStatus === 'completed').length;

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', display: 'flex', flexDirection: 'column' }}>

        {/* Top bar */}
        <Box
          sx={{
            px: { xs: 2, sm: 3 }, py: 1.5,
            background: 'linear-gradient(135deg, #0c4a6e 0%, #065f46 100%)',
            display: 'flex', alignItems: 'center', justifyContent: 'space-between',
            boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
          }}
        >
          <Stack direction="row" alignItems="center" spacing={1.5}>
            <Box
              sx={{
                width: 34, height: 34, borderRadius: 1.5,
                background: 'linear-gradient(135deg, #0ea5e9, #059669)',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}
            >
              <MedicalServices sx={{ color: '#fff', fontSize: 18 }} />
            </Box>
            <Box>
              <Typography variant="subtitle2" fontWeight={800} color="white" lineHeight={1.2}>
                DycoVue Patient Portal
              </Typography>
              {!isMobile && (
                <Typography variant="caption" color="rgba(255,255,255,0.6)">
                  Your imaging results
                </Typography>
              )}
            </Box>
          </Stack>

          <Stack direction="row" alignItems="center" spacing={1}>
            <Avatar sx={{ width: 30, height: 30, bgcolor: '#0ea5e9', fontSize: 12 }}>
              {patient?.firstName?.[0]}{patient?.lastName?.[0]}
            </Avatar>
            {!isMobile && (
              <Typography variant="body2" color="white" fontWeight={600}>
                {patient?.firstName} {patient?.lastName}
              </Typography>
            )}
            <Tooltip title="Sign Out">
              <IconButton size="small" onClick={handleLogout} sx={{ color: 'rgba(255,255,255,0.7)', '&:hover': { color: '#fca5a5' } }}>
                <Logout fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>
        </Box>

        {/* Content */}
        <Box sx={{ flex: 1, px: { xs: 2, sm: 3, md: 4 }, py: 3, maxWidth: 900, mx: 'auto', width: '100%' }}>

          {/* Welcome + summary */}
          <Typography variant="h5" fontWeight={800} mb={0.5}>
            Welcome, {patient?.firstName}
          </Typography>
          <Typography variant="body2" color="text.secondary" mb={3}>
            MRN: {patient?.mrn}
          </Typography>

          {studies.length > 0 && (
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} mb={3}>
              {[
                { label: 'Total Studies', value: studies.length, color: '#0ea5e9' },
                { label: 'Reports Ready', value: readyCount, color: '#059669' },
                { label: 'With Images', value: studies.filter(s => s.hasImages).length, color: '#6366f1' },
              ].map(card => (
                <Card
                  key={card.label}
                  elevation={0}
                  sx={{
                    flex: 1, border: `1px solid ${card.color}30`,
                    background: `linear-gradient(135deg, ${card.color}10, transparent)`,
                    borderRadius: 2.5,
                  }}
                >
                  <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                    <Typography variant="h4" fontWeight={800} sx={{ color: card.color }}>
                      {card.value}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">{card.label}</Typography>
                  </CardContent>
                </Card>
              ))}
            </Stack>
          )}

          {/* Search + refresh */}
          <Stack direction="row" spacing={1.5} mb={2} alignItems="center">
            <TextField
              placeholder="Search by type or facility…"
              size="small"
              value={search}
              onChange={e => setSearch(e.target.value)}
              sx={{ flex: 1 }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
            <Tooltip title="Refresh">
              <IconButton onClick={() => fetchStudies()} disabled={loading} size="small">
                <Refresh fontSize="small" />
              </IconButton>
            </Tooltip>
          </Stack>

          {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>{error}</Alert>}

          {loading ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress />
            </Box>
          ) : loaded && filtered.length === 0 ? (
            <Card elevation={0} sx={{ border: '1px solid', borderColor: 'divider', borderRadius: 2.5 }}>
              <CardContent sx={{ py: 6, textAlign: 'center' }}>
                <MedicalServices sx={{ fontSize: 56, color: 'text.disabled', mb: 1 }} />
                <Typography color="text.secondary">
                  {studies.length === 0
                    ? 'No imaging studies found for your patient record.'
                    : 'No studies match your search.'}
                </Typography>
              </CardContent>
            </Card>
          ) : (
            <Stack spacing={1.5}>
              {filtered.map(study => (
                <StudyCard key={study._id} study={study} />
              ))}
            </Stack>
          )}
        </Box>

        {/* Footer */}
        <Divider />
        <Box sx={{ py: 1.5, textAlign: 'center' }}>
          <Typography variant="caption" color="text.disabled">
            DycoVue Patient Portal — Read-only access to your imaging results. For queries, contact your radiology department.
          </Typography>
        </Box>
      </Box>
    </ThemeProvider>
  );
}
